import useHealth from '@hooks/health/useHealth';
import { useQuery } from '@tanstack/react-query';
import { type FC, useMemo } from 'react';
import { getMe, type UserDto } from '@api/auth';
import { getMyConsultantAccess } from '@api/consultantAccess';
import AppProviderContext from './context';

/**
 * Get user from localStorage - this is our source of truth right after login
 */
function getUserFromStorage(): UserDto | null {
    try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
            return JSON.parse(userJson) as UserDto;
        }
    } catch {
        // Ignore parse errors
    }
    return null;
}

const AppProvider: FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const health = useHealth();

    const hasToken = !!localStorage.getItem('token');
    
    // Get user from localStorage - this is our source of truth after login
    const storageUser = getUserFromStorage();

    const { 
        data: queryUser, 
        isLoading: isLoadingUser,
    } = useQuery(
        ['me'],
        getMe,
        { 
            enabled: hasToken, 
            staleTime: 5 * 60 * 1000,
            // Use localStorage user as initial data
            initialData: storageUser ?? undefined,
            refetchOnMount: 'always',
            refetchOnWindowFocus: true,
        },
    );

    // Use query data, fall back to localStorage
    const currentUser = queryUser ?? storageUser ?? undefined;
    const isConsultant = currentUser?.role === 'CONSULTANT';

    const {
        data: consultantAccessData,
        isLoading: isLoadingConsultantAccess,
        isSuccess: isConsultantAccessSuccess,
    } = useQuery(
        ['consultantAccess'],
        getMyConsultantAccess,
        {
            enabled: hasToken && isConsultant,
            // Keep stale time short so reshared hidden columns are picked up quickly
            staleTime: 30 * 1000,
            refetchOnMount: 'always',
            refetchOnWindowFocus: true,
        },
    );

    // Determine loading state
    const isFullyLoaded = useMemo(() => {
        if (!hasToken) return true;
        // If we have a user from storage, we can render immediately
        // Don't wait for query if we already know who the user is
        if (!currentUser) return false;
        // For consultants, wait for consultant access to load
        if (isConsultant && !isConsultantAccessSuccess && isLoadingConsultantAccess) {
            return false;
        }
        return true;
    }, [hasToken, currentUser, isConsultant, isConsultantAccessSuccess, isLoadingConsultantAccess]);

    // Only provide consultant access data if the query succeeded
    const consultantAccessDetails = isConsultantAccessSuccess ? (consultantAccessData ?? []) : [];
    const consultantAccess = consultantAccessDetails.map((d) => d.resourceKey);

    const value = {
        health,
        user: undefined as any,
        currentUser,
        isLoadingUser: !isFullyLoaded,
        consultantAccess,
        consultantAccessDetails,
    };

    return (
        <AppProviderContext.Provider value={value}>
            {children}
        </AppProviderContext.Provider>
    );
};

export default AppProvider;
